import {
  Conversation,
  ConversationState,
  RawApiMessage,
} from '@justrelate/llm-conversation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Language, LanguageContext, useLanguage } from './LanguageContext';
import { ChatRenderer, SystemMessage } from './components/ChatRenderer';
import { LlmConfig, buildConversationContext } from './llm';
import { useTranslate } from './translate';

import './styles/main.css';

export type ColorScheme = 'light' | 'dark' | 'system';
export type DisplayMode = 'floating' | 'overlay';
export type UserInputSource = 'freetext' | 'suggestion';

export type SendCallback = (
  userMessage: string,
  meta: { conversationDepth: number; source: UserInputSource },
) => void;

interface ChatbotProps {
  config: LlmConfig | null;
  language?: Language;
  isVisible?: boolean;
  onHide?: () => void;
  showHistory?: boolean;
  defaultDisplayMode?: DisplayMode;
  inline?: boolean;
  colorScheme?: ColorScheme;
  onSend?: SendCallback;
}

export function Chatbot({ language = 'en', ...props }: ChatbotProps) {
  return (
    <LanguageContext.Provider value={language}>
      <ChatbotWithLanguageContext {...props} />
    </LanguageContext.Provider>
  );
}

function ChatbotWithLanguageContext({
  config,
  isVisible = true,
  onHide,
  showHistory,
  defaultDisplayMode = 'floating',
  inline,
  colorScheme = 'light',
  onSend,
}: Omit<ChatbotProps, 'language'>) {
  const language = useLanguage();
  const translate = useTranslate();

  const errorMessage = useCallback(
    (error: unknown) => {
      if (!(error instanceof Error)) return JSON.stringify(error);

      if ('status' in error && error.status === 429) {
        return translate('rateLimitExceeded');
      }

      return error.toString();
    },
    [translate],
  );

  const { initialMessages, initialUserPrompt } = useMemo(
    extractInitialMessages,
    [config],
  );

  const [conversationState, setConversationState] = useState<ConversationState>(
    { messages: [], isProcessing: false },
  );

  const [systemMessage, setSystemMessage] = useState<SystemMessage | null>(
    null,
  );

  const freshConversation = useCallback(
    (messages: RawApiMessage[] = initialMessages) =>
      new Conversation(setConversationState, messages, {
        useAnonymously: config?.useAnonymously,
        systemNotice: config?.systemNotice,
        instanceId: config?.instanceId,
      }),
    [
      setConversationState,
      initialMessages,
      config?.useAnonymously,
      config?.systemNotice,
      config?.instanceId,
    ],
  );

  const startNewConversation = useCallback(() => {
    conversation.current = freshConversation();
  }, [freshConversation]);

  const sendMessage = useCallback(
    async (message: string) => {
      if (!config) return;

      try {
        await conversation.current?.processUserMessage(
          buildConversationContext(config, language),
          message,
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        const content = errorMessage(error);
        setSystemMessage({ content });
      }
    },
    [config, language, errorMessage],
  );

  const processUserMessage = useCallback(
    async (message: string, source: UserInputSource) => {
      onSend?.(message, {
        conversationDepth: conversationState.messages.filter(
          (m) => m.role === 'user',
        ).length,
        source,
      });

      await sendMessage(message);
    },
    [conversationState.messages, onSend, sendMessage],
  );

  const conversation = useRef<Conversation | null>(null);
  if (config && !conversation.current) {
    // TODO the conversation does not update when the config changes
    conversation.current = freshConversation();
  }

  const initialPromptProcessed = useRef(false);

  useEffect(() => {
    if (initialUserPrompt && !initialPromptProcessed.current) {
      sendMessage(initialUserPrompt);
      initialPromptProcessed.current = true;
    }
  }, [initialUserPrompt, sendMessage]);

  return (
    <ChatRenderer
      processUserMessage={processUserMessage}
      messages={conversationState.messages}
      config={config}
      startNewConversation={startNewConversation}
      isLoading={conversationState.isProcessing}
      systemMessage={systemMessage}
      setSystemMessage={setSystemMessage}
      showHistory={showHistory}
      isVisible={isVisible}
      onHide={onHide}
      defaultDisplayMode={defaultDisplayMode}
      inline={inline}
      colorScheme={colorScheme}
    />
  );

  function extractInitialMessages() {
    const messages = config?.initialMessages || [];
    const lastMessage = messages.at(-1);

    if (
      lastMessage?.role === 'user' &&
      typeof lastMessage.content === 'string'
    ) {
      return {
        initialMessages: messages.slice(0, -1),
        initialUserPrompt: lastMessage.content,
      };
    }

    return { initialMessages: messages, initialUserPrompt: undefined };
  }
}
